import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { TestVisibilityProvider } from "@/context/TestVisibilityContext";
import { I18nProvider } from "@/i18n/I18nContext";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Home from "@/pages/Home";
import Projects from "@/pages/Projects";
import Continuity from "@/pages/Continuity";
import ProjectWorkspace from "@/pages/ProjectWorkspace";
import Missions from "@/pages/Missions";
import MissionDetail from "@/pages/MissionDetail";
import Workflows from "@/pages/Workflows";
import WorkflowRun from "@/pages/WorkflowRun";
import Approvals from "@/pages/Approvals";
import Workforce from "@/pages/Workforce";
import AgentDetail from "@/pages/AgentDetail";
import Knowledge from "@/pages/Knowledge";
import Assets from "@/pages/Assets";
import Decisions from "@/pages/Decisions";
import Analytics from "@/pages/Analytics";
import ActivityPage from "@/pages/Activity";
import JobCenter from "@/pages/JobCenter";
import ProductionTools from "@/pages/ProductionTools";
import SettingsPage from "@/pages/Settings";
import BusinessOperations from "@/pages/BusinessOperations";
import CustomerOperations from "./pages/CustomerOperations";
import ActivateAccount from "./pages/ActivateAccount";

function Protected({ children, founderOnly = false }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen bg-[#070A12] flex items-center justify-center"><Loader2 className="animate-spin text-[#2563EB]" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (founderOnly && user.role !== "FOUNDER") return <Navigate to="/customer-operations" replace />;
  return children;
}

function StaffWorkspace() {
  const { user, logout } = useAuth();
  return <div className="min-h-screen bg-[#040914] text-white"><header className="border-b border-[#173052] px-6 py-4 flex justify-between items-center"><div><strong>MASANDI AI LABS</strong><p className="text-xs text-slate-400">Customer Operations · {user?.name}</p></div><button onClick={logout} className="text-sm text-blue-300">Keluar</button></header><main className="max-w-6xl mx-auto p-6"><CustomerOperations /></main></div>;
}

function RootRedirect() {
  const { user, ready } = useAuth();
  if (!ready) return null;
  return <Navigate to={user?.role === "STAFF" ? "/customer-operations" : "/today"} replace />;
}

function App() {
  return (
    <AuthProvider>
      <I18nProvider>
      <TestVisibilityProvider>
        <BrowserRouter>
          <Toaster position="top-right" theme="dark" />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/activate" element={<ActivateAccount />} />
            <Route path="/" element={<RootRedirect />} />
            <Route path="/customer-operations" element={<Protected><StaffWorkspace /></Protected>} />
            <Route element={<Protected founderOnly><Layout /></Protected>}>
              <Route path="/today" element={<Home />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/continuity" element={<Continuity />} />
              <Route path="/businesses" element={<BusinessOperations />} />
              <Route path="/projects/:code" element={<ProjectWorkspace />} />
              <Route path="/missions" element={<Missions />} />
              <Route path="/missions/:id" element={<MissionDetail />} />
              <Route path="/workflows" element={<Workflows />} />
              <Route path="/workflows/runs/:id" element={<WorkflowRun />} />
              <Route path="/approvals" element={<Approvals />} />
              <Route path="/workforce" element={<Workforce />} />
              <Route path="/workforce/:code" element={<AgentDetail />} />
              <Route path="/knowledge" element={<Knowledge />} />
              <Route path="/assets" element={<Assets />} />
              <Route path="/decisions" element={<Decisions />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/activity" element={<ActivityPage />} />
              <Route path="/jobs" element={<JobCenter />} />
              <Route path="/production-tools" element={<ProductionTools />} />
              <Route path="/settings" element={<SettingsPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </TestVisibilityProvider>
      </I18nProvider>
    </AuthProvider>
  );
}

export default App;
